import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Leaf, ScrollText } from "lucide-react";
import { getBooks } from "@/lib/api";
import { BookCover } from "@/components/BookCover";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const SERIES = [
  { id: "Glories of India Series", label: "Glories of India Series", accent: "maroon", Icon: ScrollText },
  { id: "Green Guru Knowledge Series", label: "Green Guru Knowledge Series", accent: "green", Icon: Leaf },
];

const TOPICS = [
  "History and Civilization",
  "Archaeology",
  "Comparative Civilizations",
  "Comparative Religion",
  "Public Awareness",
  "Environment",
  "Horticulture",
  "Gardening",
  "Landscaping",
  "Palms",
];
const LANGUAGES = ["English", "Hindi", "Bengali", "Other editions"];
const STATUSES = ["Published", "Forthcoming", "In Development"];

export default function Books() {
  useDocumentTitle("Books");
  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState("");
  const [topic, setTopic] = useState(null);
  const [language, setLanguage] = useState(null);
  const [status, setStatus] = useState(null);
  const [series, setSeries] = useState(null);
  const [sort, setSort] = useState("default");

  useEffect(() => {
    let cancelled = false;
    getBooks()
      .then((data) => { if (!cancelled) setBooks(data); })
      .catch(() => { /* catalogue renders empty state on failure */ });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let list = books.slice();
    if (series) list = list.filter(b => b.series === series);
    if (topic) list = list.filter(b => b.topics?.includes(topic));
    if (language) list = list.filter(b => b.editions?.includes(language));
    if (status) list = list.filter(b => b.status === status);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b => b.title.toLowerCase().includes(q) || (b.short_description || "").toLowerCase().includes(q));
    }
    if (sort === "title") list.sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [books, series, topic, language, status, search, sort]);

  const grouped = useMemo(() => {
    const map = { "Glories of India Series": [], "Green Guru Knowledge Series": [] };
    filtered.forEach(b => {
      const key = b.series && map[b.series] !== undefined ? b.series : "Glories of India Series";
      map[key].push(b);
    });
    return map;
  }, [filtered]);

  const clearAll = () => { setTopic(null); setLanguage(null); setStatus(null); setSeries(null); setSearch(""); };
  const activeFilterCount = [topic, language, status, series, search.trim()].filter(Boolean).length;

  return (
    <div data-testid="books-page">
      <section className="container-wide pt-16 pb-10">
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Two Collections · One Publisher</div>
        <h1 className="mt-3 font-heading text-5xl sm:text-6xl leading-[1.02]" style={{ color: "var(--gi-maroon)" }}>Book Catalogue</h1>
        <p className="mt-6 max-w-2xl text-lg" style={{ color: "var(--gi-charcoal-2)" }}>
          Eleven titles across two distinct series — scholarly civilizational inquiry and practical horticultural knowledge.
        </p>
        <div className="hair-divider mt-10" />
      </section>

      <section className="container-wide pb-24 grid gap-12 lg:grid-cols-[260px_1fr]">
        <aside className="lg:sticky lg:top-24 self-start" data-testid="books-filters">
          <FilterGroup title="Series" options={SERIES.map(s => s.label)} value={series} onChange={v => setSeries(v)} testPrefix="filter-series" />
          <FilterGroup title="Subject" options={TOPICS} value={topic} onChange={v => setTopic(v)} testPrefix="filter-topic" />
          <FilterGroup title="Language / Edition" options={LANGUAGES} value={language} onChange={v => setLanguage(v)} testPrefix="filter-lang" />
          <FilterGroup title="Status" options={STATUSES} value={status} onChange={v => setStatus(v)} testPrefix="filter-status" />
          {activeFilterCount > 0 && (
            <button onClick={clearAll} className="mt-4 btn-ghost" data-testid="filters-clear">Clear all <X size={12} /></button>
          )}
        </aside>

        <div>
          <div className="flex items-center flex-wrap gap-4 mb-10">
            <div className="flex-1 min-w-[240px] flex items-center gap-3 border px-3" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }}>
              <Search size={16} style={{ color: "var(--gi-muted)" }} />
              <input
                className="w-full bg-transparent py-3 outline-none text-sm"
                placeholder="Search titles and descriptions"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="books-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="gi-label" style={{ margin: 0 }}>Sort</span>
              <select className="gi-input" style={{ width: "auto" }} value={sort} onChange={e => setSort(e.target.value)} data-testid="books-sort">
                <option value="default">Series order</option>
                <option value="title">Title A–Z</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center" data-testid="books-empty">
              <div className="font-heading text-2xl" style={{ color: "var(--gi-maroon)" }}>No titles match your filters</div>
              <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>Try clearing filters or searching a different term.</p>
            </div>
          ) : (
            <div className="space-y-20" data-testid="books-grid">
              {SERIES.filter(s => grouped[s.id]?.length > 0).map(s => (
                <SeriesSection key={s.id} series={s} books={grouped[s.id]} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const SeriesSection = ({ series, books }) => {
  const isGreen = series.id === "Green Guru Knowledge Series";
  const accentColor = isGreen ? "var(--gi-green)" : "var(--gi-maroon)";
  const bgColor = isGreen ? "var(--gi-green-soft)" : "transparent";
  const testId = `series-section-${isGreen ? "green-guru" : "glories-of-india"}`;
  const S = series.Icon;
  return (
    <section data-testid={testId} className={isGreen ? "p-8 sm:p-10 border" : ""} style={isGreen ? { borderColor: "var(--gi-green)", background: bgColor, borderRadius: 2 } : {}}>
      <header className="flex items-center gap-3 mb-2">
        <S size={16} style={{ color: accentColor }} />
        <div className="font-accent text-[10px]" style={{ color: accentColor, letterSpacing: "0.22em" }}>Series</div>
      </header>
      <h2 className="font-heading text-3xl sm:text-4xl leading-[1.05]" style={{ color: accentColor }}>{series.label}</h2>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--gi-charcoal-2)" }}>
        {isGreen
          ? "Practical horticultural knowledge from decades of hands-on gardening, landscaping and environmental work."
          : "Civilizational inquiry through archaeology, ancient texts, geography, astronomy and living traditions."}
      </p>
      <div className="hair-divider mt-6 mb-10 max-w-md" />
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {books.map(b => (
          <Link key={b.id} to={`/books/${b.slug}`} className="block group" data-testid={`book-card-${b.slug}`}>
            <BookCover title={b.title} accent={b.cover_accent} series={b.series} coverUrl={b.cover_url} />
            <div className="mt-5">
              <div className="font-accent text-[9px] flex flex-wrap items-center gap-2" style={{ color: accentColor }}>
                <span data-testid={`book-card-status-${b.slug}`}>{b.status}</span>
                <span style={{ color: "var(--gi-divider)" }}>·</span>
                <span>{b.editions?.[0]}{b.editions?.length > 1 ? ` + ${b.editions.length - 1} more` : ""}</span>
              </div>
              <div className="mt-2 font-heading text-xl leading-tight group-hover:underline" style={{ color: "var(--gi-charcoal)" }}>{b.title}</div>
              <p className="mt-2 text-sm" style={{ color: "var(--gi-muted)" }}>{b.short_description}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const FilterGroup = ({ title, options, value, onChange, testPrefix }) => (
  <div className="mb-8">
    <div className="font-accent text-[10px] mb-4" style={{ color: "var(--gi-maroon)" }}>{title}</div>
    <div className="space-y-2">
      {options.map(o => (
        <button
          key={o}
          onClick={() => onChange(value === o ? null : o)}
          className="block text-left w-full text-sm py-1.5 transition-colors"
          style={{ color: value === o ? "var(--gi-maroon)" : "var(--gi-charcoal-2)" }}
          data-testid={`${testPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <span className="inline-block w-4">{value === o ? "◆" : "◇"}</span> {o}
        </button>
      ))}
    </div>
  </div>
);
